import React from 'react'
import type { TariffVersionDTO } from '@/shared/api'
import {
  COEF_LABELS,
  CONDITION_LABELS,
  DASH,
  coefficientConditionText,
  formatCoefficient,
  formatKm,
  formatMoney,
  formatNumber,
  formatPricePerKm,
  formatSignedMoney,
  periodLabel,
  surchargeConditionText,
} from './formatters'

// Прочерк для пустых значений: серый, чтобы отличать «нет данных» от нуля
const showValue = (text: string): React.ReactNode =>
  text === DASH ? <span className="text-gray-400">{DASH}</span> : text

// Ячейка суммы надбавки: положительные суммы — зелёным, со знаком плюс
const AmountCell: React.FC<{ value: number }> = ({ value }) => (
  <td
    className={`whitespace-nowrap py-2 pl-3 text-right text-sm ${
      value > 0 ? 'font-medium text-success-700' : 'text-gray-900'
    }`}
  >
    {formatSignedMoney(value)}
  </td>
)

interface MoneyTileProps {
  label: string
  value: number | null
}

// Спокойная плашка суммы — рядом с выделенной базовой ставкой
const MoneyTile: React.FC<MoneyTileProps> = ({ label, value }) => (
  <div className="rounded-lg border border-gray-200 px-4 py-3">
    <div className="text-xs text-gray-500">{label}</div>
    <div className="mt-1 text-xl font-medium text-gray-900">{showValue(formatMoney(value))}</div>
  </div>
)

// Витрина тарифа: только текст, без инпутов и кнопок управления —
// в просмотре логист сразу понимает, что тариф не редактируется.
export const TariffReadonlyView: React.FC<{ version: TariffVersionDTO }> = ({ version }) => {
  const distanceRule = version.distance_rule

  return (
    <div className="space-y-6">
      <section className="admin-card p-4">
        <h3 className="font-semibold">Расчёт</h3>
        <div className="mt-3 grid gap-3 md:grid-cols-3">
          <div className="rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
            <div className="text-xs font-medium uppercase tracking-wide text-primary-700">
              Базовая ставка
            </div>
            <div className="mt-1 text-3xl font-semibold text-primary-800">
              {showValue(formatMoney(version.base_rate))}
            </div>
          </div>
          <MoneyTile label="Минимальная сумма" value={version.min_amount} />
          <MoneyTile label="Максимальная сумма" value={version.max_amount} />
        </div>
      </section>

      {/* Два столбца на широком экране; на узком порядок остаётся прежним:
          фиксированные → расстояние → количество. */}
      <div className="grid items-start gap-4 xl:grid-cols-2">
        <div className="space-y-4">
          <section className="admin-card p-4">
            <h3 className="font-semibold">Фиксированные надбавки</h3>
            <p className="text-xs text-gray-500">Тип условия, значение и сумма</p>
            {version.surcharges.length === 0 ? (
              <p className="mt-3 text-sm text-gray-500">Надбавок нет</p>
            ) : (
              <table className="mt-3 w-full">
                <thead>
                  <tr className="text-xs uppercase text-gray-500">
                    <th scope="col" className="pb-2 pr-3 text-left font-medium">Тип условия</th>
                    <th scope="col" className="pb-2 pr-3 text-left font-medium">Значение</th>
                    <th scope="col" className="pb-2 pl-3 text-right font-medium">Надбавка</th>
                  </tr>
                </thead>
                <tbody>
                  {version.surcharges.map((surcharge, index) => (
                    <tr key={index} className="border-t border-gray-100">
                      <td className="py-2 pr-3 text-sm text-gray-900">
                        {showValue(CONDITION_LABELS[surcharge.condition_type] ?? DASH)}
                      </td>
                      <td className="py-2 pr-3 text-sm text-gray-500">
                        {showValue(surchargeConditionText(surcharge))}
                      </td>
                      <AmountCell value={surcharge.amount} />
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="admin-card p-4">
            <h3 className="font-semibold">Надбавка за расстояние</h3>
            <p className="text-xs text-gray-500">Условия по километражу</p>
            {distanceRule ? (
              <div className="mt-3 space-y-2">
                <ul className="space-y-1 text-sm text-gray-900">
                  <li>
                    Включено в базовую ставку:{' '}
                    <span className="font-medium">{formatKm(distanceRule.included_km)}</span>
                  </li>
                  <li>
                    Далее:{' '}
                    <span className="font-medium">
                      {formatPricePerKm(distanceRule.price_per_km)}
                    </span>
                  </li>
                </ul>
                <p className="text-xs text-gray-500">
                  Пример: 13 км при включённых 10 км и 30 ₽/км → надбавка 90 ₽.
                </p>
              </div>
            ) : (
              <p className="mt-3 text-sm text-gray-500">
                Не применяется: стоимость зависит только от базовой ставки и других условий.
              </p>
            )}
          </section>
        </div>

        <section className="admin-card p-4">
          <h3 className="font-semibold">Надбавки за количество</h3>
          <p className="text-xs text-gray-500">Порог, период и сумма</p>
          {version.order_count_rules.length === 0 ? (
            <p className="mt-3 text-sm text-gray-500">Надбавок нет</p>
          ) : (
            <table className="mt-3 w-full">
              <thead>
                <tr className="text-xs uppercase text-gray-500">
                  <th scope="col" className="pb-2 pr-3 text-left font-medium">Порог</th>
                  <th scope="col" className="pb-2 pr-3 text-left font-medium">Период</th>
                  <th scope="col" className="pb-2 pl-3 text-right font-medium">Надбавка</th>
                </tr>
              </thead>
              <tbody>
                {version.order_count_rules.map((rule, index) => (
                  <tr key={index} className="border-t border-gray-100">
                    <td className="py-2 pr-3 text-sm text-gray-900">
                      {formatNumber(rule.threshold, 0)}
                    </td>
                    <td className="py-2 pr-3 text-sm text-gray-900">
                      {showValue(periodLabel(rule.period))}
                    </td>
                    <AmountCell value={rule.amount} />
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>

      <section className="admin-card p-4">
        <h3 className="font-semibold">Коэффициенты</h3>
        {version.coefficients.length === 0 ? (
          <p className="mt-3 text-sm text-gray-500">Коэффициентов нет</p>
        ) : (
          <table className="mt-3 w-full">
            <thead>
              <tr className="text-xs uppercase text-gray-500">
                <th scope="col" className="pb-2 pr-3 text-left font-medium">Тип правила</th>
                <th scope="col" className="pb-2 pr-3 text-left font-medium">Условия применения</th>
                <th scope="col" className="pb-2 pr-3 text-right font-medium">Коэффициент</th>
                <th scope="col" className="pb-2 pl-3 text-right font-medium">Приоритет</th>
              </tr>
            </thead>
            <tbody>
              {version.coefficients.map((coefficient, index) => (
                <tr key={index} className="border-t border-gray-100">
                  <td className="py-2 pr-3 text-sm text-gray-900">
                    {showValue(COEF_LABELS[coefficient.condition_type] ?? DASH)}
                  </td>
                  <td className="py-2 pr-3 text-sm text-gray-500">
                    {coefficientConditionText(coefficient)}
                  </td>
                  <td className="whitespace-nowrap py-2 pr-3 text-right">
                    <span className="inline-block rounded bg-primary-50 px-2 py-0.5 font-medium text-primary-800">
                      {formatCoefficient(coefficient.coefficient)}
                    </span>
                  </td>
                  <td className="py-2 pl-3 text-right text-sm text-gray-900">
                    {coefficient.priority}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {version.coefficients.length > 0 && (
          <p className="mt-3 text-xs text-gray-500">
            Применяется одно подходящее правило с наибольшим числовым приоритетом. При равном
            приоритете — первое в списке. Порядок правил сохранён.
          </p>
        )}
      </section>
    </div>
  )
}
